import { RefObject, useEffect } from 'react';
import { z } from 'zod';

import assertNever from 'common/bento/lib/assertNever';
import { logEvent } from 'features/Analytics/analytics';
import logger from 'helpers/logger';

/**
 * A discriminated union matching all possible shapes of Fourthline SDK events.
 *
 * @see https://dx.fourthline.com/docs/in-depth-websdk-flow
 */
const fourthlineEvent = z.discriminatedUnion('type', [
  /**
   * The status event, giving updates on the state of the id check flow
   *
   * @see https://dx.fourthline.com/docs/in-depth-websdk-flow#status-event
   */
  z.object({
    detail: z.enum([
      'OnboardingStarted',
      'Loaded',
      'OnboardingContinuing',
      'OnboardingCompleted',
      'OnboardingCompletedError',
    ]),
    type: z.literal('flOnboardingStatus'),
  }),

  /**
   * The event emitted when a user, on mobile, requests to start the flow.
   * The "detail" key is a handler that redirects the user on the Fourthline domain.
   *
   * @see https://dx.fourthline.com/docs/embed-web-sdk#continue-to-mobile-event
   */
  z.object({
    detail: z.function().args(
      z
        .object({
          redirect: z.string(),
          redirectFailure: z.string(),
        })
        .optional(),
    ),
    type: z.literal('flContinueMobileRequest'),
  }),

  /**
   * The event emitted when the user wants to restart the flow, possibly following an invalid flow.
   * We'll handle it by re-starting the id check.
   *
   * @see https://dx.fourthline.com/docs/in-depth-websdk-flow#restart-event
   */
  z.object({ type: z.literal('flOnboardingRestartRequest') }),
]);

export const useFourthlineSdkHandlers = ({
  containerRef,
  failureRedirectUrl,
  onFlowCompleted,
  onFlowError,
  onRestartFlow,
  successRedirectUrl,
  trackingEvents,
}: {
  containerRef: RefObject<HTMLElement>;
  onFlowCompleted: () => void;
  onRestartFlow: () => void;
  successRedirectUrl: string;
  failureRedirectUrl: string;
  onFlowError?: () => void;
  trackingEvents: {
    sdkLoaded: string;
  };
}) => {
  useEffect(() => {
    const currentContainer = containerRef.current;

    const handler = (event: Event) => {
      const parsed = fourthlineEvent.safeParse(event);

      if (!parsed.success) {
        throw new Error(`Invalid Fourthline event: ${JSON.stringify(event)}`);
      }

      const eventType = parsed.data.type;

      logger.info('ℹ️ FOURTHLINE SDK EVENT', parsed.data);

      switch (eventType) {
        case 'flContinueMobileRequest': {
          const redirectHandler = parsed.data.detail;

          /**
           * -> Mobile devices only
           *
           * Calling this handler with the web-app URL means we're directed to the Fourthline domain in the current browser tab,
           * and we'll be redirected again to our own domain after the flow is done on the Fourthline domain.
           *
           * @see https://dx.fourthline.com/docs/embed-web-sdk#continue-to-mobile-event
           */
          return redirectHandler({
            redirect: successRedirectUrl,
            redirectFailure: failureRedirectUrl,
          });
        }

        case 'flOnboardingStatus': {
          const onboardingStatus = parsed.data.detail;

          switch (onboardingStatus) {
            case 'OnboardingCompleted': {
              return onFlowCompleted();
            }

            case 'Loaded': {
              logEvent({ name: trackingEvents.sdkLoaded });
              return null;
            }

            case 'OnboardingCompletedError': {
              return onFlowError?.();
            }

            case 'OnboardingContinuing':
            case 'OnboardingStarted': {
              return null;
            }
            default: {
              assertNever(onboardingStatus);
            }
          }
          return null;
        }

        case 'flOnboardingRestartRequest': {
          return onRestartFlow();
        }

        default: {
          assertNever(eventType);
        }
      }

      return null;
    };

    /**
     * Register event listeners for every events emitted by the Fourthline SDK.
     */
    fourthlineEvent.options.map((option) => {
      const eventName = option.shape.type.value;

      currentContainer?.addEventListener(eventName, handler, {
        capture: true,
        passive: true,
      });
    });

    return () => {
      fourthlineEvent.options.map((option) => {
        const eventName = option.shape.type.value;
        currentContainer?.removeEventListener(eventName, handler);
      });
    };
  }, [
    containerRef,
    onRestartFlow,
    onFlowCompleted,
    successRedirectUrl,
    failureRedirectUrl,
    onFlowError,
    trackingEvents,
  ]);
};
